import { emptyTotals, totalsInWindow, totalsInWindowSplit, } from "./model.js";
import { activityColor, countryColor } from "./theme.js";
import { formatClock, formatDayLabel, formatDuration, formatDurationCompact, formatNumber, hourTicks, } from "./time.js";
import { FONT_STACK, svgEl, textEl, truncate } from "./svg.js";
/* ------------------------------------------------------------------ */
/* Geometry constants                                                  */
/* ------------------------------------------------------------------ */
const KINDS = ["driving", "work", "rest", "availability", "noCard"];
const BAR_UNITS = {
    driving: 4,
    work: 3,
    availability: 2,
    rest: 1,
    noCard: 0.5,
};
const ROW_GAP = 9;
const RULER_H = 24;
const CONTINUOUS_RULER_H = 44;
const CONDITION_H = 18;
const COUNTRY_H = 32;
const VEHICLE_H = 30;
const DRIVER_PERIOD_H = 30;
const OVERVIEW_H = 40;
const OVERVIEW_GAP = 4;
const RIBBON_H = 12;
const DAY_HEADER_H = 20;
const DAY_TOTALS_H = 20;
const SUMMARY_H = 28;
const EVENT_LABEL_H = 16;
const EVENT_LABEL_PAD = 4;
/**
 * SVG text is enlarged relative to the original layout values; bump this to
 * make every chart label bigger in one step.
 */
const TEXT_SCALE = 1.22;
/** Average glyph width of the label sizing heuristics at {@link TEXT_SCALE}. */
const CHAR_W = 5.6 * (TEXT_SCALE / 1.12);
const TOTALS_CHAR_W = 5.2 * (TEXT_SCALE / 1.12);
const fs = (size) => Math.round(size * TEXT_SCALE * 10) / 10;
const TOP_PAD = 6;
const BOTTOM_PAD = 10;
const FULL_VIEW = { from: 0, to: 1 };
const LEFT_PAD = 12;
const PAD_RIGHT = 14;
/* ------------------------------------------------------------------ */
/* Renderer                                                            */
/* ------------------------------------------------------------------ */
export function renderChart(input) {
    const { model, options, visibility, width } = input;
    const theme = options.theme;
    const timezone = options.timezone;
    const locale = options.locale;
    const strings = options.strings;
    const prefix = `tac${input.idPrefix.replace(/[^a-zA-Z0-9_-]/g, "")}`;
    const view = clampView(input.view);
    const span = Math.max(1e-4, view.to - view.from);
    const stackedViews = input.dayViews;
    const plotX = LEFT_PAD;
    const plotW = Math.max(40, width - LEFT_PAD - PAD_RIGHT);
    const totalMinutes = Math.max(1, model.minutesTotal);
    const hits = new Map();
    const dayGeometry = new Map();
    const annotationSlots = [];
    const stackedMode = options.mode === "stacked";
    const hasCountries = model.days.some((day) => day.countries.length > 0);
    const showCountries = visibility.countries && hasCountries;
    const hasVehicles = model.days.some((day) => day.vehicles.length > 0);
    const showVehicles = visibility.vehicles && hasVehicles;
    const hasDriverPeriods = model.days.some((day) => day.driverPeriods.length > 0);
    const showDriverPeriods = visibility.driver && hasDriverPeriods;
    const coDriverDisplay = options.coDriver ?? "auto";
    const coDriverVisible = (day) => {
        if (!day.coDriverLane)
            return false;
        if (coDriverDisplay === "never")
            return false;
        if (coDriverDisplay === "always")
            return true;
        // Auto: only days where the co-driver actually had a card inserted.
        return day.coDriverLane.segments.some((segment) => !segment.noCard);
    };
    const hasCoDriverTotals = model.days.some((day) => coDriverVisible(day));
    const summaryHeight = visibility.totals ? SUMMARY_H * (hasCoDriverTotals ? 2 : 1) : 0;
    const contentTop = TOP_PAD + summaryHeight;
    const viewForDay = (day) => stackedMode ? stackedViews?.get(day.dateKey) ?? FULL_VIEW : view;
    const spanForDay = (day) => {
        const dayView = viewForDay(day);
        return Math.max(1e-4, dayView.to - dayView.from);
    };
    const sx = (day, minute) => {
        const dayView = viewForDay(day);
        return plotX + (minute / day.minutes - dayView.from) / spanForDay(day) * plotW;
    };
    const gx = (minute) => plotX + (minute / totalMinutes - view.from) / span * plotW;
    const collectEventItems = (day, xForMinute, scope) => {
        const items = [];
        const add = (label, minute, color, hitId) => {
            const text = truncate(label, 28);
            if (!text)
                return;
            const x = xForMinute(minute);
            if (x < plotX - 1 || x > plotX + plotW + 1)
                return;
            items.push({
                label: text,
                x,
                width: Math.max(34 * (TEXT_SCALE / 1.12), text.length * CHAR_W + 10),
                color,
                hitId,
            });
        };
        if (scope === "country") {
            if (visibility.countries) {
                for (const event of day.countryEvents) {
                    add(event.country, event.minute, countryColor(event.country), `country-event:${day.dateKey}:${event.minute}`);
                }
            }
            return items;
        }
        if (visibility.ferry) {
            day.ferry.forEach((marker, index) => {
                add(strings.ferry, marker.minute, theme.ferry, `ferry:${day.dateKey}:${index}`);
            });
        }
        for (const marker of day.markers) {
            if (!marker.label)
                continue;
            add(marker.label, marker.minute, marker.color || theme.mutedText, `marker:${day.dateKey}:${marker.minute}`);
        }
        return items;
    };
    const assignEventLabels = (items) => {
        items.sort((a, b) => a.x - b.x);
        const lineRight = [];
        const labels = [];
        for (const item of items) {
            const width = Math.min(item.width, Math.max(34, plotW - 4));
            const boxX = clamp(item.x - width / 2, plotX, plotX + plotW - width);
            let line = 0;
            let right = lineRight[line];
            while (right !== undefined && boxX < right + 4) {
                line += 1;
                right = lineRight[line];
            }
            lineRight[line] = boxX + width;
            labels.push({
                label: item.label,
                x: boxX,
                width,
                line,
                color: item.color,
                hitId: item.hitId,
            });
        }
        return labels;
    };
    const buildEventLabels = (day, xForMinute, scope) => assignEventLabels(collectEventItems(day, xForMinute, scope));
    /** Visible name chips of the day's plotted annotations (bands and markers). */
    const buildAnnotationLabels = (day) => {
        const items = [];
        for (const annotation of day.annotations) {
            if (annotation.type === "none" || annotation.startMinute === undefined)
                continue;
            const text = truncate(annotation.name, 26);
            if (!text)
                continue;
            const x = sx(day, annotation.startMinute);
            if (x < plotX - 1 || x > plotX + plotW + 1)
                continue;
            items.push({
                label: text,
                x,
                width: Math.max(34 * (TEXT_SCALE / 1.12), text.length * CHAR_W + 10),
                color: annotation.color || theme.mutedText,
                hitId: `annotation:${day.dateKey}:${annotation.index}`,
            });
        }
        return assignEventLabels(items);
    };
    const addTotals = (target, source) => {
        target.driving += source.driving;
        target.work += source.work;
        target.rest += source.rest;
        target.availability += source.availability;
        target.noCard += source.noCard;
    };
    const splitForView = (laneKey) => {
        const normal = emptyTotals();
        const outOfScope = emptyTotals();
        const noCardKinds = emptyTotals();
        if (stackedMode) {
            for (const day of model.days) {
                if (laneKey === "coDriverLane" && !coDriverVisible(day))
                    continue;
                const dayView = viewForDay(day);
                const split = totalsInWindowSplit(day[laneKey], dayView.from * day.minutes, dayView.to * day.minutes, day.outOfScope, visibility.noCard);
                addTotals(normal, split.normal);
                addTotals(outOfScope, split.outOfScope);
                addTotals(noCardKinds, split.noCardKinds);
            }
        }
        else {
            const from = view.from * totalMinutes;
            const to = view.to * totalMinutes;
            for (const day of model.days) {
                const offset = model.offsets.get(day.dateKey) ?? 0;
                const localFrom = Math.max(0, from - offset);
                const localTo = Math.min(day.minutes, to - offset);
                if (localTo <= localFrom)
                    continue;
                const split = totalsInWindowSplit(day[laneKey], localFrom, localTo, day.outOfScope, visibility.noCard);
                addTotals(normal, split.normal);
                addTotals(outOfScope, split.outOfScope);
                addTotals(noCardKinds, split.noCardKinds);
            }
        }
        return { normal, outOfScope, noCardKinds };
    };
    const viewSplit = splitForView("lane");
    const coDriverViewSplit = hasCoDriverTotals ? splitForView("coDriverLane") : null;
    /* ---------------- layout ---------------- */
    const stackedRows = [];
    let continuous = null;
    let height = 60;
    const anyConditions = model.days.some((day) => (visibility.outOfScope && day.outOfScope.length > 0) ||
        (visibility.ferry && day.ferry.length > 0));
    if (stackedMode) {
        let cursor = contentTop;
        for (const day of model.days) {
            const hasLane = visibility.driver && !!day.lane;
            const hasCoDriverLane = coDriverVisible(day);
            const rowHasData = hasLane ||
                hasCoDriverLane ||
                day.driverPeriods.length > 0 ||
                day.coDriverPeriods.length > 0 ||
                day.countries.length > 0 ||
                day.countryEvents.length > 0 ||
                day.vehicles.length > 0 ||
                day.outOfScope.length > 0 ||
                day.ferry.length > 0 ||
                day.markers.length > 0 ||
                day.highlights.length > 0;
            const hasVehicle = showVehicles && rowHasData;
            const hasDriverPeriods = showDriverPeriods && rowHasData;
            const hasCountry = showCountries && rowHasData;
            const top = cursor;
            const headerY = cursor;
            cursor += DAY_HEADER_H;
            const totalsY = visibility.totals ? cursor : -1;
            if (visibility.totals)
                cursor += DAY_TOTALS_H * (hasCoDriverLane ? 2 : 1);
            const coDriverTotalsY = visibility.totals && hasCoDriverLane ? totalsY + DAY_TOTALS_H : -1;
            const rulerY = cursor;
            cursor += RULER_H;
            const countryLabels = buildEventLabels(day, (minute) => sx(day, minute), "country");
            const eventLabels = buildEventLabels(day, (minute) => sx(day, minute), "other");
            const eventLines = eventLabels.length
                ? Math.max(...eventLabels.map((label) => label.line)) + 1
                : 0;
            const eventY = eventLines > 0 ? cursor : -1;
            if (eventLines > 0)
                cursor += eventLines * EVENT_LABEL_H + EVENT_LABEL_PAD;
            const eventBottom = eventLines > 0 ? eventY + eventLines * EVENT_LABEL_H + EVENT_LABEL_PAD : eventY;
            const vehicleY = hasVehicle ? cursor : -1;
            if (hasVehicle)
                cursor += VEHICLE_H;
            const vehicleBottom = hasVehicle ? vehicleY + VEHICLE_H : vehicleY;
            const driverPeriodY = hasDriverPeriods ? cursor : -1;
            if (hasDriverPeriods)
                cursor += DRIVER_PERIOD_H;
            const driverPeriodBottom = hasDriverPeriods ? driverPeriodY + DRIVER_PERIOD_H : driverPeriodY;
            const laneTop = cursor;
            if (hasLane)
                cursor += options.laneHeight;
            const laneBottom = cursor;
            const hasCoDriverPeriods = hasCoDriverLane && day.coDriverPeriods.length > 0;
            const coDriverPeriodY = hasCoDriverPeriods ? cursor : -1;
            if (hasCoDriverPeriods)
                cursor += DRIVER_PERIOD_H;
            const coDriverPeriodBottom = hasCoDriverPeriods
                ? coDriverPeriodY + DRIVER_PERIOD_H
                : coDriverPeriodY;
            const coDriverLaneTop = hasCoDriverLane ? cursor : -1;
            if (hasCoDriverLane)
                cursor += options.laneHeight;
            const coDriverLaneBottom = hasCoDriverLane ? cursor : -1;
            const countryY = hasCountry ? cursor : -1;
            if (hasCountry)
                cursor += COUNTRY_H;
            if (!hasLane && !hasCountry && !hasVehicle && !hasDriverPeriods)
                cursor += 30;
            const countryBottom = hasCountry ? countryY + COUNTRY_H : countryY;
            const hasConditions = rowHasData &&
                ((visibility.outOfScope && day.outOfScope.length > 0) ||
                    (visibility.ferry && day.ferry.length > 0));
            const conditionY = hasConditions ? cursor : -1;
            if (hasConditions)
                cursor += CONDITION_H;
            const countryLabelLines = countryLabels.length
                ? Math.max(...countryLabels.map((label) => label.line)) + 1
                : 0;
            const countryLabelY = countryLabelLines > 0 ? cursor : -1;
            if (countryLabelLines > 0)
                cursor += countryLabelLines * EVENT_LABEL_H + EVENT_LABEL_PAD;
            const countryLabelBottom = countryLabelLines > 0
                ? countryLabelY + countryLabelLines * EVENT_LABEL_H + EVENT_LABEL_PAD
                : countryLabelY;
            const hasOverview = options.navigator && spanForDay(day) < 0.999;
            const overviewY = hasOverview ? cursor + OVERVIEW_GAP : -1;
            if (hasOverview)
                cursor += OVERVIEW_GAP + OVERVIEW_H;
            // Visible names of the plotted annotations, then the annotation list.
            const annotationLabels = buildAnnotationLabels(day);
            const annotationLabelLines = annotationLabels.length
                ? Math.max(...annotationLabels.map((label) => label.line)) + 1
                : 0;
            const annotationLabelY = annotationLabelLines > 0 ? cursor : -1;
            if (annotationLabelLines > 0) {
                cursor += annotationLabelLines * EVENT_LABEL_H + EVENT_LABEL_PAD;
            }
            const annotationLabelBottom = annotationLabelLines > 0
                ? annotationLabelY + annotationLabelLines * EVENT_LABEL_H + EVENT_LABEL_PAD
                : annotationLabelY;
            const annotationHeight = Math.max(0, input.annotationHeights?.get(day.dateKey) ?? 0);
            const annotationY = annotationHeight > 0 ? cursor : -1;
            if (annotationHeight > 0)
                cursor += annotationHeight;
            stackedRows.push({
                day,
                top,
                headerY,
                totalsY,
                coDriverTotalsY,
                rulerY,
                eventY,
                eventBottom,
                eventLabels,
                vehicleY,
                vehicleBottom,
                driverPeriodY,
                driverPeriodBottom,
                laneTop,
                laneBottom,
                coDriverPeriodY,
                coDriverPeriodBottom,
                coDriverLaneTop,
                coDriverLaneBottom,
                countryY,
                countryBottom,
                conditionY,
                countryLabelY,
                countryLabelBottom,
                countryLabels,
                overviewY,
                hasOverview,
                annotationLabels,
                annotationLabelY,
                annotationLabelBottom,
                annotationY,
                annotationHeight,
                bottom: cursor,
                hasLane,
                hasCoDriverLane,
                hasVehicle,
                hasDriverPeriods,
                hasCountry,
                empty: !hasLane &&
                    !hasCoDriverLane &&
                    !hasVehicle &&
                    !hasDriverPeriods &&
                    !hasCountry &&
                    eventLabels.length === 0 &&
                    countryLabels.length === 0,
            });
            cursor += ROW_GAP;
        }
        height = Math.max(60, cursor - ROW_GAP + BOTTOM_PAD);
    }
    else {
        const lane = visibility.driver ? mergeLane(model) : null;
        const hasLane = lane !== null && model.days.some((day) => day.lane);
        const rulerHeight = CONTINUOUS_RULER_H;
        let cursor = contentTop + rulerHeight;
        const ribbonY = visibility.totals ? cursor : -1;
        if (visibility.totals)
            cursor += RIBBON_H;
        const countryItems = model.days.flatMap((day) => {
            const offset = model.offsets.get(day.dateKey) ?? 0;
            return collectEventItems(day, (minute) => gx(offset + minute), "country");
        });
        const countryLabels = assignEventLabels(countryItems);
        const eventItems = model.days.flatMap((day) => {
            const offset = model.offsets.get(day.dateKey) ?? 0;
            return collectEventItems(day, (minute) => gx(offset + minute), "other");
        });
        const eventLabels = assignEventLabels(eventItems);
        const eventLines = eventLabels.length
            ? Math.max(...eventLabels.map((label) => label.line)) + 1
            : 0;
        const eventY = eventLines > 0 ? cursor : -1;
        if (eventLines > 0)
            cursor += eventLines * EVENT_LABEL_H + EVENT_LABEL_PAD;
        const eventBottom = eventLines > 0 ? eventY + eventLines * EVENT_LABEL_H + EVENT_LABEL_PAD : eventY;
        const hasVehicle = showVehicles;
        const vehicleY = hasVehicle ? cursor : -1;
        if (hasVehicle)
            cursor += VEHICLE_H;
        const vehicleBottom = hasVehicle ? vehicleY + VEHICLE_H : vehicleY;
        const hasDriverPeriods = showDriverPeriods;
        const driverPeriodY = hasDriverPeriods ? cursor : -1;
        if (hasDriverPeriods)
            cursor += DRIVER_PERIOD_H;
        const driverPeriodBottom = hasDriverPeriods ? driverPeriodY + DRIVER_PERIOD_H : driverPeriodY;
        const laneY = cursor;
        if (hasLane)
            cursor += options.laneHeight;
        const laneBottom = cursor;
        const countryY = showCountries ? cursor : -1;
        if (showCountries)
            cursor += COUNTRY_H;
        const countryBottom = showCountries ? countryY + COUNTRY_H : countryY;
        const conditionY = anyConditions ? cursor : -1;
        if (anyConditions)
            cursor += CONDITION_H;
        const countryLabelLines = countryLabels.length
            ? Math.max(...countryLabels.map((label) => label.line)) + 1
            : 0;
        const countryLabelY = countryLabelLines > 0 ? cursor : -1;
        if (countryLabelLines > 0)
            cursor += countryLabelLines * EVENT_LABEL_H + EVENT_LABEL_PAD;
        const countryLabelBottom = countryLabelLines > 0
            ? countryLabelY + countryLabelLines * EVENT_LABEL_H + EVENT_LABEL_PAD
            : countryLabelY;
        continuous = {
            height: Math.max(60, cursor + BOTTOM_PAD),
            rulerHeight,
            ribbonY,
            eventY,
            eventBottom,
            eventLabels,
            vehicleY,
            vehicleBottom,
            driverPeriodY,
            driverPeriodBottom,
            laneY,
            laneBottom,
            countryY,
            countryBottom,
            conditionY,
            countryLabelY,
            countryLabelBottom,
            countryLabels,
            hasLane,
            hasVehicle,
            hasDriverPeriods,
            hasCountry: showCountries,
            lane,
        };
        height = continuous.height;
    }
    if (model.days.length === 0) {
        height = 90;
    }
    const continuousTrackTop = continuous
        ? continuous.hasDriverPeriods
            ? continuous.driverPeriodY
            : continuous.hasVehicle
                ? continuous.vehicleY
                : continuous.hasLane
                    ? continuous.laneY
                    : continuous.hasCountry
                        ? continuous.countryY
                        : continuous.conditionY
        : TOP_PAD;
    const continuousTrackBottom = continuous
        ? continuous.hasCountry
            ? continuous.countryBottom
            : continuous.hasLane
                ? continuous.laneBottom
                : continuous.hasDriverPeriods
                    ? continuous.driverPeriodBottom
                    : continuous.hasVehicle
                        ? continuous.vehicleBottom
                        : continuous.conditionY
        : 0;
    /* ---------------- svg skeleton ---------------- */
    const svg = svgEl("svg", {
        width,
        height,
        viewBox: `0 0 ${width} ${height}`,
        role: "group",
        "aria-label": `${options.ariaLabel} (${timezone})`,
        class: "tac-svg",
        style: `font-family:${FONT_STACK};font-variant-numeric:tabular-nums`,
    });
    svgEl("rect", { x: 0, y: 0, width, height, fill: theme.background }, svg);
    const defs = svgEl("defs", {}, svg);
    const manualPattern = svgEl("pattern", {
        id: `${prefix}-manual`,
        width: 6,
        height: 6,
        patternUnits: "userSpaceOnUse",
    }, defs);
    svgEl("circle", {
        cx: 3,
        cy: 3,
        r: 1.25,
        fill: theme.manualStripe,
    }, manualPattern);
    const noCardPattern = svgEl("pattern", {
        id: `${prefix}-nocard`,
        width: 5,
        height: 5,
        patternUnits: "userSpaceOnUse",
        patternTransform: "rotate(45)",
    }, defs);
    svgEl("line", {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 5,
        stroke: theme.noCardStripe,
        "stroke-width": 2.2,
        opacity: 0.9,
    }, noCardPattern);
    const oosPattern = svgEl("pattern", {
        id: `${prefix}-oos`,
        width: 7,
        height: 7,
        patternUnits: "userSpaceOnUse",
        patternTransform: "rotate(45)",
    }, defs);
    svgEl("rect", { width: 7, height: 7, fill: theme.outOfScope, opacity: 0.16 }, oosPattern);
    svgEl("line", {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 7,
        stroke: theme.outOfScope,
        "stroke-width": 1,
        opacity: 0.45,
    }, oosPattern);
    const clip = svgEl("clipPath", { id: `${prefix}-clip` }, defs);
    svgEl("rect", { x: plotX, y: 0, width: plotW, height }, clip);
    const underLayer = svgEl("g", {}, svg);
    const ribbonLayer = svgEl("g", { "clip-path": `url(#${prefix}-clip)` }, svg);
    const content = svgEl("g", { "clip-path": `url(#${prefix}-clip)` }, svg);
    const laneLayer = svgEl("g", {}, content);
    const gridLayer = svgEl("g", {}, content);
    const bandLayer = svgEl("g", {}, content);
    const segLayer = svgEl("g", {}, content);
    const highlightLayer = svgEl("g", {}, content);
    const vehicleLayer = svgEl("g", {}, content);
    const driverPeriodLayer = svgEl("g", {}, content);
    const countryLayer = svgEl("g", {}, content);
    const countryEventLayer = svgEl("g", {}, content);
    const markerLayer = svgEl("g", {}, content);
    const eventLayer = svgEl("g", {}, content);
    const overviewLayer = svgEl("g", {}, content);
    const condLayer = svgEl("g", {}, content);
    const ruleLayer = svgEl("g", {}, svg);
    const headerLayer = svgEl("g", {}, svg);
    const summaryLayer = svgEl("g", {}, svg);
    /* ---------------- helpers ---------------- */
    const drawActivityIcon = (parent, kind, cx, cy, size, color, title) => {
        const iconUrl = options.icons?.[kind];
        if (iconUrl) {
            const iconGroup = svgEl("g", { role: "img" }, parent);
            svgEl("image", {
                href: resolveIconHref(iconUrl),
                x: cx - size / 2,
                y: cy - size / 2,
                width: size,
                height: size,
                preserveAspectRatio: "xMidYMid meet",
            }, iconGroup);
            const iconTitle = svgEl("title", {}, iconGroup);
            iconTitle.textContent = title;
            return;
        }
        const group = svgEl("g", { role: "img" }, parent);
        const s = size / 2 - 0.5;
        switch (kind) {
            case "driving": {
                svgEl("circle", {
                    cx,
                    cy,
                    r: s,
                    fill: "none",
                    stroke: color,
                    "stroke-width": 1.15,
                }, group);
                svgEl("circle", { cx, cy, r: 1.05, fill: color }, group);
                for (const angle of [90, 210, 330]) {
                    const radians = (angle * Math.PI) / 180;
                    svgEl("line", {
                        x1: cx,
                        y1: cy,
                        x2: cx + Math.cos(radians) * s,
                        y2: cy + Math.sin(radians) * s,
                        stroke: color,
                        "stroke-width": 1.05,
                    }, group);
                }
                break;
            }
            case "work": {
                svgEl("line", {
                    x1: cx - 4,
                    y1: cy + 4,
                    x2: cx + 2.5,
                    y2: cy - 2.5,
                    stroke: color,
                    "stroke-width": 1.35,
                    "stroke-linecap": "round",
                }, group);
                svgEl("line", {
                    x1: cx + 4,
                    y1: cy + 4,
                    x2: cx - 2.5,
                    y2: cy - 2.5,
                    stroke: color,
                    "stroke-width": 1.35,
                    "stroke-linecap": "round",
                }, group);
                svgEl("line", {
                    x1: cx + 1.2,
                    y1: cy - 3.2,
                    x2: cx + 3.2,
                    y2: cy - 1.2,
                    stroke: color,
                    "stroke-width": 1.35,
                    "stroke-linecap": "round",
                }, group);
                svgEl("line", {
                    x1: cx - 1.2,
                    y1: cy - 3.2,
                    x2: cx - 3.2,
                    y2: cy - 1.2,
                    stroke: color,
                    "stroke-width": 1.35,
                    "stroke-linecap": "round",
                }, group);
                break;
            }
            case "availability": {
                svgEl("rect", {
                    x: cx - 3.6,
                    y: cy - 3.6,
                    width: 7.2,
                    height: 7.2,
                    rx: 0.6,
                    fill: "none",
                    stroke: color,
                    "stroke-width": 1.3,
                }, group);
                break;
            }
            case "rest": {
                svgEl("path", {
                    d: `M ${cx - 4.5} ${cy + 3.5} V ${cy - 2.5} H ${cx - 1.5} V ${cy + 0.5} H ${cx + 4.5} V ${cy + 3.5} Z`,
                    fill: "none",
                    stroke: color,
                    "stroke-width": 1.15,
                    "stroke-linejoin": "round",
                }, group);
                svgEl("line", {
                    x1: cx - 4.5,
                    y1: cy + 3.5,
                    x2: cx + 4.5,
                    y2: cy + 3.5,
                    stroke: color,
                    "stroke-width": 1.15,
                }, group);
                break;
            }
            default: {
                svgEl("rect", {
                    x: cx - 4,
                    y: cy - 3,
                    width: 8,
                    height: 6,
                    rx: 1,
                    fill: "none",
                    stroke: color,
                    "stroke-width": 1.15,
                }, group);
                svgEl("line", {
                    x1: cx - 3,
                    y1: cy + 2.5,
                    x2: cx + 3,
                    y2: cy - 2.5,
                    stroke: color,
                    "stroke-width": 1.15,
                }, group);
                break;
            }
        }
        const titleElement = svgEl("title", {}, group);
        titleElement.textContent = title;
    };
    const drawTotalsGroup = (parent, x, label, labelColor, totals, y, kinds, showZero, inView, titlePrefix) => {
        const items = kinds.filter((kind) => showZero || totals[kind] > 0.5);
        if (items.length === 0)
            return x;
        const labelElement = textEl(parent, x, y, label, {
            fill: labelColor,
            "font-size": fs(8),
            "font-weight": 700,
            "letter-spacing": 0.4,
        });
        if (inView) {
            const title = svgEl("title", {}, labelElement);
            title.textContent = strings.totalsInView;
        }
        let cursor = x + Math.max(36 * (TEXT_SCALE / 1.12), label.length * TOTALS_CHAR_W + 10);
        for (const kind of items) {
            const color = kind === "noCard" ? theme.noCardStripe : activityColor(theme, kind);
            const value = formatDurationCompact(totals[kind]);
            drawActivityIcon(parent, kind, cursor + 6.5, y - 4, 13, color, `${titlePrefix}${kindLabel(kind, strings)} ${value}`);
            textEl(parent, cursor + 18, y, value, {
                fill: totals[kind] > 0.5 ? theme.text : theme.mutedText,
                "font-size": fs(8.5),
                "font-variant-numeric": "tabular-nums",
            });
            cursor += 50;
        }
        return cursor;
    };
    /**
     * One totals line: optional lane label, the normal buckets, the
     * out-of-scope split and the no-card bucket. Returns the x after the last
     * group so consecutive rows stay aligned.
     */
    const drawSplitTotals = (parent, x, y, label, split, inView) => {
        const normalKinds = ["driving", "work", "availability", "rest"];
        let cursor = x;
        if (label) {
            textEl(parent, x, y, label, {
                fill: theme.mutedText,
                "font-size": fs(8),
                "font-weight": 700,
                "letter-spacing": 0.4,
            });
            cursor = x + 62;
        }
        cursor = drawTotalsGroup(parent, cursor, strings.withDriverCard, theme.mutedText, split.normal, y, normalKinds, true, inView, "");
        const oosKinds = KINDS.filter((kind) => kind !== "noCard" && split.outOfScope[kind] > 0.5);
        if (oosKinds.length > 0) {
            cursor += 22;
            svgEl("line", {
                x1: cursor - 10,
                y1: y - 10,
                x2: cursor - 10,
                y2: y + 3,
                stroke: theme.border,
                "stroke-width": 1,
            }, parent);
            cursor = drawTotalsGroup(parent, cursor, strings.outOfScopeShort, theme.outOfScope, split.outOfScope, y, oosKinds, false, inView, `${strings.outOfScope}: `);
        }
        if (visibility.noCard) {
            cursor += 22;
            svgEl("line", {
                x1: cursor - 10,
                y1: y - 10,
                x2: cursor - 10,
                y2: y + 3,
                stroke: theme.border,
                "stroke-width": 1,
            }, parent);
            const noCardKinds = normalKinds.filter((kind) => split.noCardKinds[kind] > 0.5);
            if (noCardKinds.length > 0) {
                cursor = drawTotalsGroup(parent, cursor, strings.withoutDriverCard, theme.mutedText, split.noCardKinds, y, noCardKinds, false, inView, `${strings.withoutDriverCard}: `);
                if (split.noCardKinds.noCard > 0.5) {
                    // No-card time without a recorded activity: value only, no icon.
                    textEl(parent, cursor + 2, y, formatDurationCompact(split.noCardKinds.noCard), {
                        fill: theme.text,
                        "font-size": fs(8.5),
                        "font-variant-numeric": "tabular-nums",
                    });
                    cursor += 50;
                }
            }
            else {
                const labelWidth = Math.max(36 * (TEXT_SCALE / 1.12), strings.withoutDriverCard.length * TOTALS_CHAR_W + 10);
                textEl(parent, cursor, y, strings.withoutDriverCard, {
                    fill: theme.mutedText,
                    "font-size": fs(8),
                    "font-weight": 700,
                    "letter-spacing": 0.4,
                });
                textEl(parent, cursor + labelWidth, y, formatDurationCompact(split.noCardKinds.noCard), {
                    fill: split.noCardKinds.noCard > 0.5 ? theme.text : theme.mutedText,
                    "font-size": fs(8.5),
                    "font-variant-numeric": "tabular-nums",
                });
                cursor += labelWidth + 50;
            }
        }
        return cursor;
    };
    /**
     * `369 097 → 369 771 km · Σ 674 km` for the header/summary. The delta is the
     * distance covered on the row/period; it is only shown when both ends exist.
     */
    const odoLabel = (odoStart, odoEnd) => {
        if (odoStart === null && odoEnd === null)
            return "";
        const start = odoStart !== null ? formatNumber(odoStart, locale) : "?";
        const end = odoEnd !== null ? formatNumber(odoEnd, locale) : "?";
        let label = `${start} → ${end} ${strings.km}`;
        if (odoStart !== null && odoEnd !== null) {
            label += ` · Σ ${formatNumber(odoEnd - odoStart, locale)} ${strings.km}`;
        }
        return label;
    };
    const drawSummary = () => {
        if (!visibility.totals)
            return;
        const y = TOP_PAD + 15;
        drawSplitTotals(summaryLayer, LEFT_PAD, y, (hasCoDriverTotals ? strings.driver : strings.totals).toUpperCase(), viewSplit, span < 0.999);
        if (coDriverViewSplit) {
            drawSplitTotals(summaryLayer, LEFT_PAD, y + SUMMARY_H, strings.coDriver.toUpperCase(), coDriverViewSplit, span < 0.999);
        }
        if (!stackedMode && continuous?.lane) {
            const lane = continuous.lane;
            const label = odoLabel(lane.odoStart, lane.odoEnd);
            if (label) {
                textEl(summaryLayer, width - PAD_RIGHT, y, label, {
                    fill: theme.mutedText,
                    "font-size": fs(8.5),
                    "text-anchor": "end",
                });
            }
        }
        svgEl("line", {
            x1: 0,
            y1: TOP_PAD + summaryHeight,
            x2: width,
            y2: TOP_PAD + summaryHeight,
            stroke: theme.border,
            "stroke-width": 1,
        }, summaryLayer);
    };
    drawSummary();
    const drawDayTotals = (row) => {
        if (!visibility.totals || !row.day.lane || row.totalsY < 0)
            return;
        const day = row.day;
        const dayView = viewForDay(day);
        const inView = spanForDay(day) < 0.999;
        const hasCoDriver = row.hasCoDriverLane;
        const driverSplit = totalsInWindowSplit(day.lane, dayView.from * day.minutes, dayView.to * day.minutes, day.outOfScope, visibility.noCard);
        drawSplitTotals(headerLayer, LEFT_PAD, row.totalsY + 13, hasCoDriver ? strings.driver.toUpperCase() : null, driverSplit, inView);
        if (hasCoDriver && row.coDriverTotalsY >= 0) {
            const coDriverSplit = totalsInWindowSplit(day.coDriverLane, dayView.from * day.minutes, dayView.to * day.minutes, day.outOfScope, visibility.noCard);
            drawSplitTotals(headerLayer, LEFT_PAD, row.coDriverTotalsY + 13, strings.coDriver.toUpperCase(), coDriverSplit, inView);
        }
    };
    const drawSegment = (day, segment, x1, x2, y) => {
        const w = Math.max(1.4, x2 - x1);
        const maxH = options.laneHeight - 22;
        if (maxH <= 2)
            return;
        const units = options.barHeightMode === "specific" ? BAR_UNITS[segment.kind] : 4;
        const h = Math.max(2, (maxH * units) / 4);
        const barY = y + maxH - h;
        const selected = segment.id === input.selectedId;
        const group = svgEl("g", {
            "data-hit": segment.id,
            class: "tac-seg",
            role: "button",
            tabindex: 0,
            "aria-label": `${kindLabel(segment.kind, strings)}, ${formatClock(segment.start, timezone)} – ${formatClock(segment.end, timezone)}, ${formatDuration((segment.end - segment.start) / 60)}`,
        }, segLayer);
        const fill = activityColor(theme, segment.kind);
        svgEl("rect", { x: x1, y: barY, width: w, height: h, rx: 2.5, fill }, group);
        if (segment.noCard) {
            svgEl("rect", {
                x: x1,
                y: barY,
                width: w,
                height: h,
                rx: 2.5,
                fill: `url(#${prefix}-nocard)`,
                "pointer-events": "none",
            }, group);
        }
        else if (segment.manual) {
            svgEl("rect", {
                x: x1,
                y: barY,
                width: w,
                height: h,
                rx: 2.5,
                fill: `url(#${prefix}-manual)`,
                "pointer-events": "none",
            }, group);
        }
        if (selected) {
            svgEl("rect", {
                x: x1 + 0.75,
                y: barY + 0.75,
                width: Math.max(0.5, w - 1.5),
                height: Math.max(0.5, h - 1.5),
                rx: 2,
                fill: "none",
                stroke: theme.selection,
                "stroke-width": 1.6,
                "pointer-events": "none",
            }, group);
        }
        if (options.showSegmentLabels && w >= 50 && h >= 18) {
            const label = kindLabel(segment.kind, strings);
            const showTime = w >= 138;
            const caption = showTime
                ? `${label} ${formatClock(segment.start, timezone)}–${formatClock(segment.end, timezone)}`
                : label;
            textEl(group, x1 + w / 2, barY + h / 2 + 3, caption, {
                "text-anchor": "middle",
                "font-size": fs(showTime ? 8.2 : 9),
                "font-weight": 600,
                fill: segment.kind === "availability" ? "#1f2937" : "#ffffff",
                "pointer-events": "none",
            });
        }
        hits.set(segment.id, { kind: "segment", day, segment });
    };
    const drawVehicleRange = (day, range, index, x1, x2, y) => {
        const w = x2 - x1;
        if (w < 0.6)
            return;
        if (x2 < plotX || x1 > plotX + plotW)
            return;
        const accent = theme.vehicle;
        const id = `vehicle:${day.dateKey}:${index}`;
        const group = svgEl("g", { "data-hit": id, class: "tac-vehicle" }, vehicleLayer);
        svgEl("rect", {
            x: x1,
            y: y + 3,
            width: w,
            height: VEHICLE_H - 6,
            rx: 3,
            fill: accent,
            "fill-opacity": 0.12,
            stroke: accent,
            "stroke-opacity": 0.4,
            "stroke-width": 0.8,
        }, group);
        svgEl("line", {
            x1,
            y1: y + 4,
            x2,
            y2: y + 4,
            stroke: accent,
            "stroke-width": 1.5,
            opacity: 0.85,
        }, group);
        svgEl("rect", {
            x: x1,
            y: y + 3,
            width: Math.min(3, w),
            height: VEHICLE_H - 6,
            rx: 1.5,
            fill: accent,
        }, group);
        const continuesBefore = range.start > range.periodStart + 0.5;
        const continuesAfter = range.end < range.periodEnd - 0.5;
        const displayName = `${continuesBefore ? "… " : ""}${range.registrationNumber}${continuesAfter ? " …" : ""}`;
        const visibleX2 = Math.min(x2, plotX + plotW);
        const labelX = Math.max(x1 + 8, plotX + 8);
        const labelWidth = Math.max(0, visibleX2 - labelX - 4);
        if (labelWidth >= 26) {
            textEl(group, labelX, y + 15, truncate(displayName, Math.max(3, Math.floor(labelWidth / 6))), {
                fill: theme.text,
                "font-size": fs(9),
                "font-weight": 600,
                "pointer-events": "none",
            });
        }
        if (labelWidth >= 92) {
            const endLabel = range.end >= day.dayEnd ? "24:00" : formatClock(range.end, timezone);
            const caption = `${formatClock(range.start, timezone)}–${endLabel} · ${formatDuration(range.endMinute - range.startMinute)}`;
            textEl(group, labelX, y + 25, truncate(caption, Math.max(4, Math.floor(labelWidth / 5))), {
                fill: theme.mutedText,
                "font-size": fs(7.8),
                "pointer-events": "none",
            });
        }
        const title = svgEl("title", {}, group);
        title.textContent = `${range.registrationNumber} · ${formatClock(range.start, timezone)}–${formatClock(range.end, timezone)}`;
        hits.set(id, { kind: "vehicle", day, range });
    };
    const drawVehicleBoundaries = (day, xForMinute, top, bottom) => {
        const minutes = new Map();
        for (const range of day.vehicles) {
            for (const minute of [range.startMinute, range.endMinute]) {
                if (minute > 0.5 && minute < day.minutes - 0.5) {
                    minutes.set(Math.round(minute * 10) / 10, minute);
                }
            }
        }
        for (const minute of [...minutes.values()].sort((a, b) => a - b)) {
            const x = xForMinute(minute);
            if (x < plotX || x > plotX + plotW)
                continue;
            svgEl("line", {
                x1: x,
                y1: top,
                x2: x,
                y2: bottom,
                stroke: theme.mutedText,
                "stroke-width": 1,
                "stroke-dasharray": "2 3",
                opacity: 0.28,
            }, vehicleLayer);
        }
    };
    const drawEventLabelRow = (labels, top, bottom) => {
        if (labels.length === 0 || top < 0)
            return;
        svgEl("rect", {
            x: plotX,
            y: top,
            width: plotW,
            height: Math.max(1, bottom - top),
            fill: theme.surfaceAlt,
            "fill-opacity": 0.65,
            "pointer-events": "none",
        }, eventLayer);
        svgEl("line", {
            x1: plotX,
            y1: bottom,
            x2: plotX + plotW,
            y2: bottom,
            stroke: theme.border,
            "stroke-width": 1,
        }, eventLayer);
        for (const label of labels) {
            const y = top + label.line * EVENT_LABEL_H + 2;
            const group = svgEl("g", {
                class: "tac-event-label",
                ...(label.hitId ? { "data-hit": label.hitId } : {}),
            }, eventLayer);
            svgEl("rect", {
                x: label.x,
                y,
                width: label.width,
                height: EVENT_LABEL_H - 3,
                rx: 2.5,
                fill: theme.background,
                "fill-opacity": 0.95,
                stroke: label.color,
                "stroke-opacity": 0.6,
                "stroke-width": 0.9,
            }, group);
            textEl(group, label.x + 5, y + 10, label.label, {
                fill: theme.text,
                "font-size": fs(9),
                "font-weight": 600,
                "pointer-events": "none",
            });
        }
    };
    const drawHighlight = (day, range, x1, x2, top, bottom) => {
        const w = x2 - x1;
        if (w < 0.5 || bottom <= top)
            return;
        const color = range.color || theme.outOfScope;
        const id = `highlight:${day.dateKey}:${range.startMinute}:${range.endMinute}`;
        const group = svgEl("g", { "data-hit": id, class: "tac-highlight" }, highlightLayer);
        svgEl("rect", {
            x: x1,
            y: top,
            width: w,
            height: Math.max(1, bottom - top),
            fill: color,
            "fill-opacity": 0.18,
            "pointer-events": "none",
        }, group);
        // Hover target at the top edge so the band does not block segment clicks.
        svgEl("rect", {
            x: x1,
            y: top,
            width: w,
            height: Math.min(10, Math.max(1, bottom - top)),
            fill: "transparent",
        }, group);
        hits.set(id, { kind: "highlight", day, range });
    };
    const drawMarker = (day, marker, x, top, bottom) => {
        if (x < plotX - 1 || x > plotX + plotW + 1)
            return;
        const color = marker.color || theme.mutedText;
        const id = `marker:${day.dateKey}:${marker.minute}`;
        const group = svgEl("g", { "data-hit": id, class: "tac-marker" }, markerLayer);
        svgEl("line", {
            x1: x,
            y1: top,
            x2: x,
            y2: bottom,
            stroke: color,
            "stroke-width": 1.4,
            "stroke-dasharray": marker.dashed ? "4 3" : undefined,
            opacity: 0.8,
        }, group);
        svgEl("rect", {
            x: x - 4,
            y: top,
            width: 8,
            height: Math.max(1, bottom - top),
            fill: "transparent",
        }, group);
        const title = svgEl("title", {}, group);
        title.textContent = marker.label || "";
        hits.set(id, { kind: "marker", day, marker });
    };
    /** Band of a `range` annotation, with the annotation hit target. */
    const drawAnnotationRange = (day, annotation, x1, x2, top, bottom) => {
        if (annotation.startMinute === undefined || annotation.endMinute === undefined)
            return;
        const w = x2 - x1;
        if (w < 0.5 || bottom <= top)
            return;
        const color = annotation.color || theme.selection || theme.outOfScope;
        const id = `annotation:${day.dateKey}:${annotation.index}`;
        const group = svgEl("g", { "data-hit": id, class: "tac-annotation-range" }, highlightLayer);
        svgEl("rect", {
            x: x1,
            y: top,
            width: w,
            height: Math.max(1, bottom - top),
            fill: color,
            "fill-opacity": 0.16,
            "pointer-events": "none",
        }, group);
        svgEl("rect", {
            x: x1,
            y: top,
            width: w,
            height: Math.min(10, Math.max(1, bottom - top)),
            fill: "transparent",
        }, group);
        const title = svgEl("title", {}, group);
        title.textContent = annotation.description || annotation.name;
        hits.set(id, { kind: "annotation", day, annotation });
    };
    /** Vertical line of a `timestamp` annotation, with the annotation hit target. */
    const drawAnnotationMarker = (day, annotation, x, top, bottom) => {
        if (x < plotX - 1 || x > plotX + plotW + 1)
            return;
        const color = annotation.color || theme.mutedText;
        const id = `annotation:${day.dateKey}:${annotation.index}`;
        const group = svgEl("g", { "data-hit": id, class: "tac-annotation-marker" }, markerLayer);
        svgEl("line", {
            x1: x,
            y1: top,
            x2: x,
            y2: bottom,
            stroke: color,
            "stroke-width": 1.4,
            "stroke-dasharray": "3 3",
            opacity: 0.9,
        }, group);
        svgEl("rect", {
            x: x - 4,
            y: top,
            width: 8,
            height: Math.max(1, bottom - top),
            fill: "transparent",
        }, group);
        const title = svgEl("title", {}, group);
        title.textContent = annotation.description || annotation.name;
        hits.set(id, { kind: "annotation", day, annotation });
    };
    const drawDriverPeriodRange = (day, range, index, x1, x2, y, prefix = "driver-period") => {
        const w = x2 - x1;
        if (w < 0.6)
            return;
        if (x2 < plotX || x1 > plotX + plotW)
            return;
        const accent = theme.selection;
        const id = `${prefix}:${day.dateKey}:${index}`;
        const group = svgEl("g", { "data-hit": id, class: "tac-driver-period" }, driverPeriodLayer);
        svgEl("rect", {
            x: x1,
            y: y + 3,
            width: w,
            height: DRIVER_PERIOD_H - 6,
            rx: 3,
            fill: accent,
            "fill-opacity": 0.08,
            stroke: accent,
            "stroke-opacity": 0.32,
            "stroke-width": 0.8,
        }, group);
        svgEl("line", {
            x1,
            y1: y + 4,
            x2,
            y2: y + 4,
            stroke: accent,
            "stroke-width": 1.5,
            opacity: 0.8,
        }, group);
        svgEl("rect", {
            x: x1,
            y: y + 3,
            width: Math.min(3, w),
            height: DRIVER_PERIOD_H - 6,
            rx: 1.5,
            fill: accent,
        }, group);
        const continuesBefore = range.start > range.periodStart + 0.5;
        const continuesAfter = range.end < range.periodEnd - 0.5;
        const displayName = `${continuesBefore ? "… " : ""}${range.driverName}${continuesAfter ? " …" : ""}`;
        const visibleX2 = Math.min(x2, plotX + plotW);
        const labelX = Math.max(x1 + 8, plotX + 8);
        const labelWidth = Math.max(0, visibleX2 - labelX - 4);
        if (labelWidth >= 26) {
            textEl(group, labelX, y + 15, truncate(displayName, Math.max(3, Math.floor(labelWidth / 6))), {
                fill: theme.text,
                "font-size": fs(9),
                "font-weight": 600,
                "pointer-events": "none",
            });
        }
        if (labelWidth >= 92) {
            const endLabel = range.end >= day.dayEnd ? "24:00" : formatClock(range.end, timezone);
            const caption = `${formatClock(range.start, timezone)}–${endLabel} · ${formatDuration(range.endMinute - range.startMinute)}`;
            textEl(group, labelX, y + 25, truncate(caption, Math.max(4, Math.floor(labelWidth / 5))), {
                fill: theme.mutedText,
                "font-size": fs(7.8),
                "pointer-events": "none",
            });
        }
        const title = svgEl("title", {}, group);
        title.textContent = `${range.driverName} · ${formatClock(range.start, timezone)}–${formatClock(range.end, timezone)}`;
        hits.set(id, { kind: "driverPeriod", day, range });
    };
    const drawDriverPeriodBoundaries = (day, xForMinute, top, bottom, periods = day.driverPeriods) => {
        const minutes = new Map();
        for (const range of periods) {
            for (const minute of [range.startMinute, range.endMinute]) {
                if (minute > 0.5 && minute < day.minutes - 0.5) {
                    minutes.set(Math.round(minute * 10) / 10, minute);
                }
            }
        }
        for (const minute of [...minutes.values()].sort((a, b) => a - b)) {
            const x = xForMinute(minute);
            if (x < plotX || x > plotX + plotW)
                continue;
            svgEl("line", {
                x1: x,
                y1: top,
                x2: x,
                y2: bottom,
                stroke: theme.mutedText,
                "stroke-width": 1,
                "stroke-dasharray": "2 3",
                opacity: 0.28,
            }, driverPeriodLayer);
        }
    };
    const drawCountryRange = (day, range, index, x1, x2, y) => {
        const w = x2 - x1;
        if (w < 0.6)
            return;
        if (x2 < plotX || x1 > plotX + plotW)
            return;
        const accent = countryColor(range.country);
        const id = `country:${day.dateKey}:${index}`;
        const group = svgEl("g", { "data-hit": id, class: "tac-country" }, countryLayer);
        svgEl("rect", {
            x: x1,
            y: y + 3,
            width: w,
            height: COUNTRY_H - 6,
            rx: 3,
            fill: accent,
            "fill-opacity": 0.2,
            stroke: accent,
            "stroke-opacity": 0.45,
            "stroke-width": 0.8,
        }, group);
        svgEl("line", {
            x1,
            y1: y + 4,
            x2,
            y2: y + 4,
            stroke: accent,
            "stroke-width": 1.5,
            opacity: 0.9,
        }, group);
        svgEl("rect", {
            x: x1,
            y: y + 3,
            width: Math.min(3, w),
            height: COUNTRY_H - 6,
            rx: 1.5,
            fill: accent,
        }, group);
        const continuesBefore = range.start > range.periodStart + 0.5;
        const continuesAfter = range.end < range.periodEnd - 0.5;
        const displayName = `${continuesBefore ? "… " : ""}${range.country || strings.unknownCountry}${continuesAfter ? " …" : ""}`;
        const visibleX2 = Math.min(x2, plotX + plotW);
        const labelX = Math.max(x1 + 8, plotX + 8);
        const labelWidth = Math.max(0, visibleX2 - labelX - 4);
        if (labelWidth >= 26) {
            textEl(group, labelX, y + 14, truncate(displayName, Math.max(3, Math.floor(labelWidth / 5.3))), {
                fill: theme.text,
                "font-size": fs(9),
                "font-weight": 600,
                "pointer-events": "none",
            });
        }
        if (labelWidth >= 84) {
            const endLabel = range.end >= day.dayEnd ? "24:00" : formatClock(range.end, timezone);
            const caption = `${formatClock(range.start, timezone)}–${endLabel} · ${formatDuration(range.endMinute - range.startMinute)}`;
            textEl(group, labelX, y + 23, truncate(caption, Math.max(4, Math.floor(labelWidth / 4.5))), {
                fill: theme.mutedText,
                "font-size": fs(7.8),
                "pointer-events": "none",
            });
        }
        hits.set(id, { kind: "country", day, range });
    };
    const drawCountryEvent = (day, event, x, top, bottom) => {
        if (x < plotX - 1 || x > plotX + plotW + 1)
            return;
        const accent = countryColor(event.country);
        const id = `country-event:${day.dateKey}:${event.minute}`;
        const group = svgEl("g", { "data-hit": id, class: "tac-country-event" }, countryEventLayer);
        svgEl("line", {
            x1: x,
            y1: top,
            x2: x,
            y2: bottom,
            stroke: accent,
            "stroke-width": 1.5,
            "stroke-dasharray": "4 3",
            opacity: 0.9,
        }, group);
        svgEl("circle", {
            cx: x,
            cy: Math.max(top + 5, bottom - 8),
            r: 3,
            fill: accent,
            stroke: theme.background,
            "stroke-width": 1.2,
        }, group);
        // A wider transparent hit target so the thin marker is easy to hover.
        svgEl("rect", {
            x: x - 4,
            y: top,
            width: 8,
            height: Math.max(1, bottom - top),
            fill: "transparent",
        }, group);
        const title = svgEl("title", {}, group);
        title.textContent = `${event.country} · ${formatClock(event.time, timezone)}`;
        hits.set(id, { kind: "countryEvent", day, event });
    };
    const drawCountryBoundaries = (day, xForMinute, top, bottom) => {
        const minutes = new Map();
        for (const range of day.countries) {
            for (const minute of [range.startMinute, range.endMinute]) {
                if (minute > 0.5 && minute < day.minutes - 0.5) {
                    minutes.set(Math.round(minute * 10) / 10, minute);
                }
            }
        }
        for (const minute of [...minutes.values()].sort((a, b) => a - b)) {
            const x = xForMinute(minute);
            if (x < plotX || x > plotX + plotW)
                continue;
            svgEl("line", {
                x1: x,
                y1: top,
                x2: x,
                y2: bottom,
                stroke: theme.mutedText,
                "stroke-width": 1,
                "stroke-dasharray": "2 3",
                opacity: 0.35,
            }, countryLayer);
        }
    };
    const drawDayOverview = (day, overviewY) => {
        const dayView = viewForDay(day);
        const trackY = overviewY + 14;
        const trackH = 26;
        const group = svgEl("g", {
            "data-day-nav": "1",
            "data-day-nav-key": day.dateKey,
            class: "tac-day-nav",
        }, overviewLayer);
        svgEl("rect", {
            x: plotX,
            y: trackY,
            width: plotW,
            height: trackH,
            rx: 4,
            fill: theme.noData,
        }, group);
        if (visibility.driver && day.lane) {
            for (const segment of day.lane.segments) {
                if (!visibility.noCard && segment.noCard)
                    continue;
                const x1 = plotX + (segment.startMinute / day.minutes) * plotW;
                const x2 = plotX + (segment.endMinute / day.minutes) * plotW;
                svgEl("rect", {
                    x: x1,
                    y: trackY + 3,
                    width: Math.max(0.4, x2 - x1),
                    height: 9,
                    fill: activityColor(theme, segment.kind),
                    opacity: segment.noCard ? 0.7 : 0.9,
                }, group);
            }
        }
        if (visibility.vehicles) {
            for (const vehicle of day.vehicles) {
                const x1 = plotX + (vehicle.startMinute / day.minutes) * plotW;
                const x2 = plotX + (vehicle.endMinute / day.minutes) * plotW;
                svgEl("rect", {
                    x: x1,
                    y: trackY + 15,
                    width: Math.max(0.4, x2 - x1),
                    height: 5,
                    fill: theme.vehicle,
                    opacity: 0.75,
                }, group);
            }
        }
        if (visibility.countries) {
            for (const country of day.countries) {
                const x1 = plotX + (country.startMinute / day.minutes) * plotW;
                const x2 = plotX + (country.endMinute / day.minutes) * plotW;
                svgEl("rect", {
                    x: x1,
                    y: trackY + 21,
                    width: Math.max(0.4, x2 - x1),
                    height: 5,
                    fill: countryColor(country.country),
                    opacity: 0.75,
                }, group);
            }
        }
        svgEl("rect", {
            x: plotX,
            y: trackY,
            width: plotW,
            height: trackH,
            fill: "transparent",
        }, group);
        const windowX1 = plotX + dayView.from * plotW;
        const windowX2 = plotX + dayView.to * plotW;
        if (windowX1 > plotX) {
            svgEl("rect", {
                x: plotX,
                y: trackY,
                width: windowX1 - plotX,
                height: trackH,
                fill: theme.background,
                opacity: 0.55,
            }, group);
        }
        if (windowX2 < plotX + plotW) {
            svgEl("rect", {
                x: windowX2,
                y: trackY,
                width: plotX + plotW - windowX2,
                height: trackH,
                fill: theme.background,
                opacity: 0.55,
            }, group);
        }
        const windowGroup = svgEl("g", { "data-day-nav-window": "1", class: "tac-day-nav-window" }, group);
        svgEl("rect", {
            x: windowX1,
            y: trackY,
            width: Math.max(2, windowX2 - windowX1),
            height: trackH,
            rx: 2,
            fill: theme.selection,
            "fill-opacity": 0.06,
            stroke: theme.selection,
            "stroke-width": 1.4,
        }, windowGroup);
        svgEl("rect", {
            "data-day-nav-handle": "start",
            class: "tac-day-nav-handle",
            x: windowX1 - 4,
            y: trackY - 1,
            width: 8,
            height: trackH + 2,
            rx: 2,
            fill: theme.selection,
        }, windowGroup);
        svgEl("rect", {
            "data-day-nav-handle": "end",
            class: "tac-day-nav-handle",
            x: windowX2 - 4,
            y: trackY - 1,
            width: 8,
            height: trackH + 2,
            rx: 2,
            fill: theme.selection,
        }, windowGroup);
        const startClock = formatClock(day.dayStart + dayView.from * day.minutes * 60, timezone);
        const endClock = dayView.to >= 0.999
            ? "24:00"
            : formatClock(day.dayStart + dayView.to * day.minutes * 60, timezone);
        const caption = textEl(overviewLayer, plotX + 2, overviewY + 10, `${strings.overview.toUpperCase()}  ${startClock} – ${endClock}`, {
            fill: theme.mutedText,
            "font-size": fs(8),
            "font-weight": 600,
            "letter-spacing": 0.35,
            "font-variant-numeric": "tabular-nums",
        });
        const captionTitle = svgEl("title", {}, caption);
        captionTitle.textContent = `${strings.overview} ${startClock} – ${endClock}`;
    };
    /* ---------------- empty chart ---------------- */
    if (model.days.length === 0) {
        textEl(svg, width / 2, 48, strings.noActivity, {
            "text-anchor": "middle",
            "font-size": fs(12),
            fill: theme.mutedText,
        });
        return { svg, height, hits, plot: { x: plotX, width: plotW }, dayGeometry, annotationSlots };
    }
    /* ---------------- stacked mode ---------------- */
    if (stackedMode) {
        stackedRows.forEach((row, index) => {
            const day = row.day;
            if (index % 2 === 1) {
                svgEl("rect", {
                    x: 0,
                    y: row.top,
                    width,
                    height: row.bottom - row.top + ROW_GAP,
                    fill: theme.surfaceAlt,
                }, underLayer);
            }
            svgEl("line", {
                x1: 0,
                y1: row.bottom + ROW_GAP / 2,
                x2: width,
                y2: row.bottom + ROW_GAP / 2,
                stroke: theme.border,
                "stroke-width": 1,
            }, underLayer);
            textEl(headerLayer, LEFT_PAD, row.headerY + 13, formatDayLabel(day.dayStart, timezone, locale), {
                fill: theme.text,
                "font-size": fs(11),
                "font-weight": 600,
            });
            const headerParts = [];
            if (options.showDayMeta) {
                // A no-data row keeps the vehicle identity but not the driver names:
                // the roster can still be present on an empty bucket.
                const meta = row.empty
                    ? day.input.meta?.registrationNumber
                    : [day.input.meta?.registrationNumber, day.input.meta?.note]
                        .filter((part) => !!part)
                        .join(" · ");
                if (meta)
                    headerParts.push(meta);
            }
            if (day.lane && (day.lane.odoStart !== null || day.lane.odoEnd !== null)) {
                const label = odoLabel(day.lane.odoStart, day.lane.odoEnd);
                if (label)
                    headerParts.push(label);
            }
            if (headerParts.length > 0) {
                textEl(headerLayer, width - PAD_RIGHT, row.headerY + 13, truncate(headerParts.join(" · "), 86), {
                    fill: theme.mutedText,
                    "font-size": fs(8.5),
                    "text-anchor": "end",
                });
            }
            drawDayTotals(row);
            const ticks = hourTicks(day.dayStart, day.dayEnd, timezone);
            const pixelsPerHour = (plotW / day.minutes) * (60 / spanForDay(day));
            const labelStep = labelStepFor(pixelsPerHour);
            const gridStep = gridStepFor(pixelsPerHour);
            const gridTop = row.hasDriverPeriods
                ? row.driverPeriodY
                : row.hasVehicle
                    ? row.vehicleY
                    : row.hasLane
                        ? row.laneTop
                        : row.hasCoDriverLane
                            ? row.coDriverLaneTop
                            : row.hasCountry
                                ? row.countryY
                                : row.conditionY;
            const gridBottom = row.hasCountry
                ? row.countryBottom
                : row.hasCoDriverLane
                    ? row.coDriverLaneBottom
                    : row.hasLane
                        ? row.laneBottom
                        : row.hasDriverPeriods
                            ? row.driverPeriodBottom
                            : row.hasVehicle
                                ? row.vehicleBottom
                                : row.conditionY;
            for (const tick of ticks) {
                const x = sx(day, tick.minute);
                const labelVisible = x >= plotX - 1 && x <= plotX + plotW + 1;
                if (options.showRulers && tick.hour % labelStep === 0 && labelVisible) {
                    let labelX = x;
                    let anchor = "middle";
                    if (tick.hour === 0 && x <= plotX + 1) {
                        labelX = plotX + 2;
                        anchor = "start";
                    }
                    else if (tick.hour === 24 && x >= plotX + plotW - 1) {
                        labelX = plotX + plotW - 2;
                        anchor = "end";
                    }
                    else if (x < plotX + 24) {
                        labelX = x;
                        anchor = "start";
                    }
                    else if (x > plotX + plotW - 24) {
                        labelX = x;
                        anchor = "end";
                    }
                    if (labelX >= plotX - 1 && labelX <= plotX + plotW + 1) {
                        textEl(ruleLayer, labelX, row.rulerY + 15, tick.label, {
                            fill: theme.axisText,
                            "font-size": fs(8.5),
                            "text-anchor": anchor,
                        });
                    }
                }
                if (tick.hour === 24)
                    continue;
                if (gridBottom > gridTop) {
                    svgEl("line", {
                        x1: x,
                        y1: gridTop,
                        x2: x,
                        y2: gridBottom,
                        stroke: theme.grid,
                        "stroke-width": 1,
                    }, gridLayer);
                    if (gridStep < 60) {
                        for (let minute = gridStep; minute < 60; minute += gridStep) {
                            const subX = sx(day, tick.minute + minute);
                            svgEl("line", {
                                x1: subX,
                                y1: gridTop,
                                x2: subX,
                                y2: gridBottom,
                                stroke: theme.grid,
                                "stroke-width": 1,
                                opacity: 0.6,
                            }, gridLayer);
                        }
                    }
                }
            }
            for (const marker of day.dst) {
                const x = sx(day, marker.minute);
                const group = svgEl("g", {}, bandLayer);
                svgEl("line", {
                    x1: x,
                    y1: gridTop,
                    x2: x,
                    y2: gridBottom,
                    stroke: theme.dst,
                    "stroke-width": 1.2,
                    "stroke-dasharray": "2 3",
                    opacity: 0.9,
                }, group);
                const title = svgEl("title", {}, group);
                title.textContent = marker.direction === 1 ? strings.dstForward : strings.dstBack;
            }
            if (row.hasVehicle) {
                svgEl("rect", {
                    x: plotX,
                    y: row.vehicleY + 3,
                    width: plotW,
                    height: VEHICLE_H - 6,
                    rx: 4,
                    fill: theme.noData,
                }, laneLayer);
            }
            if (row.hasDriverPeriods) {
                svgEl("rect", {
                    x: plotX,
                    y: row.driverPeriodY + 3,
                    width: plotW,
                    height: DRIVER_PERIOD_H - 6,
                    rx: 4,
                    fill: theme.noData,
                }, laneLayer);
            }
            if (row.hasCoDriverLane && row.coDriverPeriodY >= 0) {
                svgEl("rect", {
                    x: plotX,
                    y: row.coDriverPeriodY + 3,
                    width: plotW,
                    height: DRIVER_PERIOD_H - 6,
                    rx: 4,
                    fill: theme.noData,
                }, laneLayer);
            }
            if (row.hasLane && day.lane) {
                svgEl("rect", {
                    x: plotX,
                    y: row.laneTop + 5,
                    width: plotW,
                    height: options.laneHeight - 10,
                    rx: 4,
                    fill: theme.noData,
                }, laneLayer);
            }
            if (row.hasCoDriverLane && day.coDriverLane) {
                svgEl("rect", {
                    x: plotX,
                    y: row.coDriverLaneTop + 5,
                    width: plotW,
                    height: options.laneHeight - 10,
                    rx: 4,
                    fill: theme.noData,
                }, laneLayer);
                textEl(segLayer, plotX + plotW - 8, row.coDriverLaneTop + 13, strings.coDriver, {
                    fill: theme.mutedText,
                    "font-size": fs(8.5),
                    "text-anchor": "end",
                });
            }
            if (row.hasCountry) {
                svgEl("rect", {
                    x: plotX,
                    y: row.countryY + 3,
                    width: plotW,
                    height: COUNTRY_H - 6,
                    rx: 4,
                    fill: theme.noData,
                }, laneLayer);
            }
            if (row.empty) {
                svgEl("rect", {
                    x: plotX,
                    y: row.laneTop + 4,
                    width: plotW,
                    height: 22,
                    rx: 4,
                    fill: theme.noData,
                }, laneLayer);
                textEl(segLayer, plotX + 10, row.laneTop + 18, strings.noActivity, {
                    fill: theme.mutedText,
                    "font-size": fs(9),
                });
            }
            drawEventLabelRow(row.eventLabels, row.eventY, row.eventBottom);
            drawEventLabelRow(row.countryLabels, row.countryLabelY, row.countryLabelBottom);
            drawEventLabelRow(row.annotationLabels, row.annotationLabelY, row.annotationLabelBottom);
            for (const annotation of day.annotations) {
                if (annotation.type !== "range")
                    continue;
                if (annotation.startMinute === undefined || annotation.endMinute === undefined)
                    continue;
                drawAnnotationRange(day, annotation, sx(day, annotation.startMinute), sx(day, annotation.endMinute), gridTop, gridBottom);
            }
            for (const range of day.highlights) {
                drawHighlight(day, range, sx(day, range.startMinute), sx(day, range.endMinute), gridTop, gridBottom);
            }
            if (visibility.outOfScope && (row.hasLane || row.hasCoDriverLane)) {
                const activityTop = row.hasLane ? row.laneTop : row.coDriverLaneTop;
                const activityBottom = row.hasCoDriverLane ? row.coDriverLaneBottom : row.laneBottom;
                day.outOfScope.forEach((range, rangeIndex) => {
                    const x1 = Math.max(plotX, sx(day, range.startMinute));
                    const x2 = Math.min(plotX + plotW, sx(day, range.endMinute));
                    if (x2 - x1 < 0.5)
                        return;
                    const id = `oos:${day.dateKey}:${rangeIndex}`;
                    const group = svgEl("g", { "data-hit": id, class: "tac-cond" }, bandLayer);
                    svgEl("rect", {
                        x: x1,
                        y: activityTop,
                        width: x2 - x1,
                        height: Math.max(1, activityBottom - activityTop),
                        fill: `url(#${prefix}-oos)`,
                    }, group);
                    hits.set(id, { kind: "outOfScope", day, range });
                });
            }
            if (row.hasLane && day.lane) {
                let visibleCount = 0;
                for (const segment of day.lane.segments) {
                    if (!visibility.noCard && segment.noCard)
                        continue;
                    visibleCount += 1;
                    drawSegment(day, segment, sx(day, segment.startMinute), sx(day, segment.endMinute), row.laneTop + 11);
                }
                if (visibleCount === 0) {
                    textEl(segLayer, plotX + 10, row.laneTop + options.laneHeight / 2 + 3, strings.noActivity, {
                        fill: theme.mutedText,
                        "font-size": fs(9),
                    });
                }
            }
            if (row.hasCoDriverLane && day.coDriverLane) {
                let visibleCount = 0;
                for (const segment of day.coDriverLane.segments) {
                    if (!visibility.noCard && segment.noCard)
                        continue;
                    visibleCount += 1;
                    drawSegment(day, segment, sx(day, segment.startMinute), sx(day, segment.endMinute), row.coDriverLaneTop + 11);
                }
                if (visibleCount === 0) {
                    textEl(segLayer, plotX + 10, row.coDriverLaneTop + options.laneHeight / 2 + 3, strings.noActivity, {
                        fill: theme.mutedText,
                        "font-size": fs(9),
                    });
                }
            }
            if (row.hasVehicle) {
                if (day.vehicles.length === 0) {
                    textEl(segLayer, plotX + 10, row.vehicleY + 20, strings.noVehicle, {
                        fill: theme.mutedText,
                        "font-size": fs(9),
                    });
                }
                day.vehicles.forEach((range, rangeIndex) => {
                    drawVehicleRange(day, range, rangeIndex, sx(day, range.startMinute), sx(day, range.endMinute), row.vehicleY);
                });
                const vehicleBottom = row.hasCountry
                    ? row.countryBottom
                    : row.hasLane
                        ? row.laneBottom
                        : row.vehicleBottom;
                drawVehicleBoundaries(day, (minute) => sx(day, minute), row.vehicleY, vehicleBottom);
            }
            if (row.hasDriverPeriods) {
                if (day.driverPeriods.length === 0) {
                    textEl(segLayer, plotX + 10, row.driverPeriodY + 20, strings.noDriverPeriod, {
                        fill: theme.mutedText,
                        "font-size": fs(9),
                    });
                }
                day.driverPeriods.forEach((range, rangeIndex) => {
                    drawDriverPeriodRange(day, range, rangeIndex, sx(day, range.startMinute), sx(day, range.endMinute), row.driverPeriodY);
                });
                const driverPeriodBottom = row.hasCountry
                    ? row.countryBottom
                    : row.hasLane
                        ? row.laneBottom
                        : row.driverPeriodBottom;
                drawDriverPeriodBoundaries(day, (minute) => sx(day, minute), row.driverPeriodY, driverPeriodBottom);
            }
            if (row.hasCoDriverLane && row.coDriverPeriodY >= 0) {
                day.coDriverPeriods.forEach((range, rangeIndex) => {
                    drawDriverPeriodRange(day, range, rangeIndex, sx(day, range.startMinute), sx(day, range.endMinute), row.coDriverPeriodY, "co-driver-period");
                });
                drawDriverPeriodBoundaries(day, (minute) => sx(day, minute), row.coDriverPeriodY, row.coDriverLaneBottom, day.coDriverPeriods);
            }
            if (row.hasCountry) {
                if (day.countries.length === 0) {
                    textEl(segLayer, plotX + 10, row.countryY + 19, strings.noCountry, {
                        fill: theme.mutedText,
                        "font-size": fs(9),
                    });
                }
                day.countries.forEach((range, rangeIndex) => {
                    drawCountryRange(day, range, rangeIndex, sx(day, range.startMinute), sx(day, range.endMinute), row.countryY);
                });
                const boundaryTop = row.hasLane ? row.laneTop : row.countryY;
                drawCountryBoundaries(day, (minute) => sx(day, minute), boundaryTop, row.countryBottom);
            }
            if (visibility.countries) {
                for (const event of day.countryEvents) {
                    drawCountryEvent(day, event, sx(day, event.minute), row.eventBottom >= 0 ? row.eventBottom : gridTop, gridBottom);
                }
            }
            for (const marker of day.markers) {
                const markerTop = row.eventBottom >= 0 ? row.eventBottom : gridTop;
                drawMarker(day, marker, sx(day, marker.minute), markerTop, gridBottom);
            }
            for (const annotation of day.annotations) {
                if (annotation.type !== "timestamp")
                    continue;
                if (annotation.startMinute === undefined)
                    continue;
                drawAnnotationMarker(day, annotation, sx(day, annotation.startMinute), row.eventBottom >= 0 ? row.eventBottom : gridTop, gridBottom);
            }
            if (visibility.ferry) {
                for (const marker of day.ferry) {
                    const x = sx(day, marker.minute);
                    svgEl("line", {
                        x1: x,
                        y1: row.eventBottom >= 0 ? row.eventBottom : gridTop,
                        x2: x,
                        y2: gridBottom,
                        stroke: theme.ferry,
                        "stroke-width": 1.4,
                        "stroke-dasharray": "4 3",
                        opacity: 0.85,
                    }, condLayer);
                }
            }
            if (row.conditionY >= 0) {
                drawConditionRow(row.conditionY, day, sx, theme, strings, hits, condLayer, visibility);
            }
            if (row.overviewY >= 0)
                drawDayOverview(day, row.overviewY);
            dayGeometry.set(day.dateKey, {
                x: plotX,
                width: plotW,
                dayStart: day.dayStart,
                minutes: day.minutes,
                top: row.top,
                bottom: row.bottom,
                view: viewForDay(day),
            });
            // The annotation list starts at `annotationY`; before the first
            // measurement the area has no height and sits at the row's natural end.
            annotationSlots.push({
                date: day.dateKey,
                index,
                dayStart: day.dayStart,
                top: row.annotationY >= 0 ? row.annotationY : row.bottom,
                height: row.annotationHeight,
            });
        });
    }
    /* ---------------- continuous mode ---------------- */
    if (!stackedMode && continuous) {
        const firstDay = model.days[0];
        const pixelsPerHour = (plotW / totalMinutes) * (60 / span);
        const labelStep = labelStepFor(pixelsPerHour);
        const gridStep = gridStepFor(pixelsPerHour);
        svgEl("rect", {
            x: 0,
            y: contentTop,
            width,
            height: continuous.rulerHeight,
            fill: theme.surface,
        }, ruleLayer);
        for (let index = 0; index < model.days.length; index += 1) {
            const day = model.days[index];
            if (!day)
                continue;
            const offset = model.offsets.get(day.dateKey) ?? 0;
            const dayX = gx(offset);
            const dayW = gx(offset + day.minutes) - dayX;
            if (dayW > 46) {
                textEl(ruleLayer, dayX + 4, contentTop + 15, formatDayLabel(day.dayStart, timezone, locale), {
                    fill: theme.text,
                    "font-size": fs(10.5),
                    "font-weight": 600,
                });
            }
            if (index > 0) {
                svgEl("line", {
                    x1: dayX,
                    y1: contentTop + 18,
                    x2: dayX,
                    y2: height,
                    stroke: theme.border,
                    "stroke-width": 1,
                }, ruleLayer);
            }
            const ticks = hourTicks(day.dayStart, day.dayEnd, timezone);
            for (const tick of ticks) {
                const x = gx(offset + tick.minute);
                const isFirstMidnight = tick.hour === 0 && index === 0;
                const labelVisible = x >= plotX - 1 && x <= plotX + plotW + 1;
                if (options.showRulers &&
                    labelVisible &&
                    tick.hour % labelStep === 0 &&
                    tick.hour !== 24 &&
                    (tick.hour !== 0 || isFirstMidnight)) {
                    let labelX = x;
                    let anchor = "middle";
                    if (isFirstMidnight && x <= plotX + 1) {
                        labelX = plotX + 2;
                        anchor = "start";
                    }
                    else if (x < plotX + 24) {
                        labelX = x;
                        anchor = "start";
                    }
                    else if (x > plotX + plotW - 24) {
                        labelX = x;
                        anchor = "end";
                    }
                    if (labelX >= plotX - 1 && labelX <= plotX + plotW + 1) {
                        textEl(ruleLayer, labelX, contentTop + 33, tick.label, {
                            fill: theme.axisText,
                            "font-size": fs(8.5),
                            "text-anchor": anchor,
                        });
                    }
                }
                if (tick.hour === 24)
                    continue;
                if (continuousTrackBottom > continuousTrackTop) {
                    svgEl("line", {
                        x1: x,
                        y1: continuousTrackTop,
                        x2: x,
                        y2: continuousTrackBottom,
                        stroke: theme.grid,
                        "stroke-width": 1,
                    }, gridLayer);
                    if (gridStep < 60) {
                        for (let minute = gridStep; minute < 60; minute += gridStep) {
                            const subX = gx(offset + tick.minute + minute);
                            svgEl("line", {
                                x1: subX,
                                y1: continuousTrackTop,
                                x2: subX,
                                y2: continuousTrackBottom,
                                stroke: theme.grid,
                                "stroke-width": 1,
                                opacity: 0.6,
                            }, gridLayer);
                        }
                    }
                }
            }
            dayGeometry.set(day.dateKey, {
                x: dayX,
                width: dayW,
                dayStart: day.dayStart,
                minutes: day.minutes,
            });
        }
        if (options.showRulers) {
            const endX = gx(totalMinutes);
            if (endX >= plotX && endX <= plotX + plotW + 1) {
                textEl(ruleLayer, plotX + plotW - 2, contentTop + 33, "24:00", {
                    fill: theme.axisText,
                    "font-size": fs(8.5),
                    "text-anchor": "end",
                });
            }
        }
        if (!firstDay || model.minutesTotal <= 0) {
            textEl(svg, width / 2, 52, strings.noActivity, {
                "text-anchor": "middle",
                "font-size": fs(12),
                fill: theme.mutedText,
            });
        }
        drawEventLabelRow(continuous.eventLabels, continuous.eventY, continuous.eventBottom);
        drawEventLabelRow(continuous.countryLabels, continuous.countryLabelY, continuous.countryLabelBottom);
        for (const day of model.days) {
            const offset = model.offsets.get(day.dateKey) ?? 0;
            for (const range of day.highlights) {
                drawHighlight(day, range, gx(offset + range.startMinute), gx(offset + range.endMinute), continuousTrackTop, continuousTrackBottom);
            }
            for (const marker of day.dst) {
                const x = gx(offset + marker.minute);
                const group = svgEl("g", {}, bandLayer);
                svgEl("line", {
                    x1: x,
                    y1: continuousTrackTop,
                    x2: x,
                    y2: continuousTrackBottom,
                    stroke: theme.dst,
                    "stroke-width": 1.2,
                    "stroke-dasharray": "2 3",
                    opacity: 0.9,
                }, group);
                const title = svgEl("title", {}, group);
                title.textContent = marker.direction === 1 ? strings.dstForward : strings.dstBack;
            }
            if (visibility.outOfScope && continuous.hasLane) {
                day.outOfScope.forEach((range, rangeIndex) => {
                    const x1 = Math.max(plotX, gx(offset + range.startMinute));
                    const x2 = Math.min(plotX + plotW, gx(offset + range.endMinute));
                    if (x2 - x1 < 0.5)
                        return;
                    const id = `oos:${day.dateKey}:${rangeIndex}`;
                    const group = svgEl("g", { "data-hit": id, class: "tac-cond" }, bandLayer);
                    svgEl("rect", {
                        x: x1,
                        y: continuous.laneY,
                        width: x2 - x1,
                        height: Math.max(1, continuous.laneBottom - continuous.laneY),
                        fill: `url(#${prefix}-oos)`,
                    }, group);
                    hits.set(id, { kind: "outOfScope", day, range });
                });
            }
        }
        if (visibility.totals) {
            for (const day of model.days) {
                const offset = model.offsets.get(day.dateKey) ?? 0;
                const localFrom = Math.max(0, view.from * totalMinutes - offset);
                const localTo = Math.min(day.minutes, view.to * totalMinutes - offset);
                if (localTo <= localFrom)
                    continue;
                const cellX = gx(offset + localFrom);
                const cellW = gx(offset + localTo) - cellX;
                if (cellW <= 0.5)
                    continue;
                const totals = totalsInWindow(day.lane, localFrom, localTo, visibility.noCard);
                const sum = KINDS.reduce((acc, kind) => acc + totals[kind], 0);
                const id = `totals:${day.dateKey}`;
                const group = svgEl("g", { "data-hit": id, class: "tac-ribbon" }, ribbonLayer);
                svgEl("rect", {
                    x: cellX,
                    y: continuous.ribbonY + 2,
                    width: Math.max(0.5, cellW - 0.5),
                    height: 8,
                    rx: 2,
                    fill: theme.noData,
                }, group);
                if (sum > 0) {
                    let cursor = cellX;
                    for (const kind of KINDS) {
                        const value = totals[kind];
                        if (value <= 0)
                            continue;
                        const partW = ((cellW - 0.5) * value) / sum;
                        if (partW <= 0.15)
                            continue;
                        svgEl("rect", {
                            x: cursor,
                            y: continuous.ribbonY + 2,
                            width: partW,
                            height: 8,
                            fill: kind === "noCard" ? `url(#${prefix}-nocard)` : activityColor(theme, kind),
                        }, group);
                        cursor += partW;
                    }
                }
                hits.set(id, { kind: "totals", day, totals });
            }
        }
        if (continuous.hasVehicle) {
            svgEl("rect", {
                x: plotX,
                y: continuous.vehicleY + 3,
                width: plotW,
                height: VEHICLE_H - 6,
                rx: 4,
                fill: theme.noData,
            }, laneLayer);
        }
        if (continuous.hasDriverPeriods) {
            svgEl("rect", {
                x: plotX,
                y: continuous.driverPeriodY + 3,
                width: plotW,
                height: DRIVER_PERIOD_H - 6,
                rx: 4,
                fill: theme.noData,
            }, laneLayer);
        }
        if (continuous.hasLane && continuous.lane) {
            svgEl("rect", {
                x: plotX,
                y: continuous.laneY + 5,
                width: plotW,
                height: options.laneHeight - 10,
                rx: 4,
                fill: theme.noData,
            }, laneLayer);
        }
        if (continuous.hasCountry) {
            svgEl("rect", {
                x: plotX,
                y: continuous.countryY + 3,
                width: plotW,
                height: COUNTRY_H - 6,
                rx: 4,
                fill: theme.noData,
            }, laneLayer);
        }
        if (!continuous.hasLane && !continuous.hasCountry && !continuous.hasVehicle && !continuous.hasDriverPeriods) {
            textEl(svg, plotX + plotW / 2, continuous.laneY + 26, strings.noActivity, {
                "text-anchor": "middle",
                "font-size": fs(12),
                fill: theme.mutedText,
            });
        }
        for (const day of model.days) {
            const offset = model.offsets.get(day.dateKey) ?? 0;
            if (continuous.hasLane && day.lane) {
                for (const segment of day.lane.segments) {
                    if (!visibility.noCard && segment.noCard)
                        continue;
                    drawSegment(day, segment, gx(offset + segment.startMinute), gx(offset + segment.endMinute), continuous.laneY + 11);
                }
            }
            if (continuous.hasVehicle) {
                if (day.vehicles.length === 0) {
                    textEl(segLayer, gx(offset) + 6, continuous.vehicleY + 20, strings.noVehicle, {
                        fill: theme.mutedText,
                        "font-size": fs(9),
                    });
                }
                day.vehicles.forEach((range, rangeIndex) => {
                    drawVehicleRange(day, range, rangeIndex, gx(offset + range.startMinute), gx(offset + range.endMinute), continuous.vehicleY);
                });
                const vehicleBottom = continuous.hasCountry
                    ? continuous.countryBottom
                    : continuous.hasLane
                        ? continuous.laneBottom
                        : continuous.vehicleBottom;
                drawVehicleBoundaries(day, (minute) => gx(offset + minute), continuous.vehicleY, vehicleBottom);
            }
            if (continuous.hasDriverPeriods) {
                if (day.driverPeriods.length === 0) {
                    textEl(segLayer, gx(offset) + 6, continuous.driverPeriodY + 20, strings.noDriverPeriod, {
                        fill: theme.mutedText,
                        "font-size": fs(9),
                    });
                }
                day.driverPeriods.forEach((range, rangeIndex) => {
                    drawDriverPeriodRange(day, range, rangeIndex, gx(offset + range.startMinute), gx(offset + range.endMinute), continuous.driverPeriodY);
                });
                const driverPeriodBottom = continuous.hasCountry
                    ? continuous.countryBottom
                    : continuous.hasLane
                        ? continuous.laneBottom
                        : continuous.driverPeriodBottom;
                drawDriverPeriodBoundaries(day, (minute) => gx(offset + minute), continuous.driverPeriodY, driverPeriodBottom);
            }
            if (continuous.hasCountry) {
                if (day.countries.length === 0) {
                    textEl(segLayer, gx(offset) + 6, continuous.countryY + 19, strings.noCountry, {
                        fill: theme.mutedText,
                        "font-size": fs(9),
                    });
                }
                day.countries.forEach((range, rangeIndex) => {
                    drawCountryRange(day, range, rangeIndex, gx(offset + range.startMinute), gx(offset + range.endMinute), continuous.countryY);
                });
                drawCountryBoundaries(day, (minute) => gx(offset + minute), continuous.laneY, continuous.countryBottom);
            }
            if (visibility.countries) {
                for (const event of day.countryEvents) {
                    drawCountryEvent(day, event, gx(offset + event.minute), continuous.eventBottom >= 0 ? continuous.eventBottom : continuousTrackTop, continuousTrackBottom);
                }
            }
        }
        for (const day of model.days) {
            const offset = model.offsets.get(day.dateKey) ?? 0;
            for (const marker of day.markers) {
                const markerTop = continuous.eventBottom >= 0 ? continuous.eventBottom : continuousTrackTop;
                drawMarker(day, marker, gx(offset + marker.minute), markerTop, continuousTrackBottom);
            }
        }
        if (visibility.ferry) {
            for (const day of model.days) {
                const offset = model.offsets.get(day.dateKey) ?? 0;
                for (const marker of day.ferry) {
                    const x = gx(offset + marker.minute);
                    svgEl("line", {
                        x1: x,
                        y1: continuous.eventBottom >= 0 ? continuous.eventBottom : continuousTrackTop,
                        x2: x,
                        y2: continuousTrackBottom,
                        stroke: theme.ferry,
                        "stroke-width": 1.4,
                        "stroke-dasharray": "4 3",
                        opacity: 0.85,
                    }, condLayer);
                }
            }
        }
        if (continuous.conditionY >= 0) {
            for (const day of model.days) {
                const offset = model.offsets.get(day.dateKey) ?? 0;
                if (visibility.outOfScope) {
                    day.outOfScope.forEach((range) => {
                        const x1 = Math.max(plotX, gx(offset + range.startMinute));
                        const x2 = Math.min(plotX + plotW, gx(offset + range.endMinute));
                        if (x2 - x1 < 1)
                            return;
                        drawConditionLabel(condLayer, (x1 + x2) / 2, continuous.conditionY + 12, x2 - x1, strings.outOfScope, strings.outOfScopeShort, theme.outOfScope);
                    });
                }
                if (visibility.ferry) {
                    day.ferry.forEach((marker, markerIndex) => {
                        const x = gx(offset + marker.minute);
                        const id = `ferry:${day.dateKey}:${markerIndex}`;
                        drawFerryIcon(condLayer, x, continuous.conditionY, theme, id, day, marker, hits);
                    });
                }
            }
        }
    }
    return { svg, height, hits, plot: { x: plotX, width: plotW }, dayGeometry, annotationSlots };
}
/* ------------------------------------------------------------------ */
/* Small drawing helpers                                               */
/* ------------------------------------------------------------------ */
function drawConditionRow(conditionY, day, sx, theme, strings, hits, parent, visibility) {
    if (visibility.outOfScope) {
        for (const range of day.outOfScope) {
            const x1 = sx(day, range.startMinute);
            const x2 = sx(day, range.endMinute);
            if (x2 - x1 < 2)
                continue;
            drawConditionLabel(parent, (x1 + x2) / 2, conditionY + 12, x2 - x1, strings.outOfScope, strings.outOfScopeShort, theme.outOfScope);
        }
    }
    if (visibility.ferry) {
        day.ferry.forEach((marker, index) => {
            const x = sx(day, marker.minute);
            const id = `ferry:${day.dateKey}:${index}`;
            drawFerryIcon(parent, x, conditionY, theme, id, day, marker, hits);
        });
    }
}
function drawConditionLabel(parent, centerX, baseline, width, label, shortLabel, color) {
    if (width < 26)
        return;
    const caption = width >= 74 ? label : shortLabel;
    textEl(parent, centerX, baseline, caption, {
        "text-anchor": "middle",
        "font-size": fs(8),
        "font-weight": 600,
        fill: color,
        stroke: "none",
    });
}
function drawFerryIcon(parent, x, conditionY, theme, id, day, marker, hits) {
    const group = svgEl("g", { "data-hit": id, class: "tac-cond" }, parent);
    svgEl("path", {
        d: `M ${x - 5} ${conditionY + 9} h 10 l -2 4 h -6 z`,
        fill: theme.ferry,
    }, group);
    svgEl("path", {
        d: `M ${x} ${conditionY + 2} v 7 l 4.5 -1 z`,
        fill: theme.ferry,
    }, group);
    hits.set(id, { kind: "ferry", day, marker });
}
export function resolveIconHref(url) {
    try {
        return new URL(url, document.baseURI).href;
    }
    catch {
        return url;
    }
}
function mergeLane(model) {
    let summary = null;
    for (const day of model.days) {
        const lane = day.lane;
        if (!lane)
            continue;
        if (!summary) {
            summary = {
                label: lane.label,
                driverName: lane.driverName,
                cardNumber: lane.cardNumber,
                odoStart: lane.odoStart,
                odoEnd: lane.odoEnd,
                totals: emptyTotals(),
                kind: lane.segments[0]?.kind ?? "driving",
            };
        }
        if (!summary.label && lane.label)
            summary.label = lane.label;
        if (!summary.driverName && lane.driverName)
            summary.driverName = lane.driverName;
        if (!summary.cardNumber && lane.cardNumber)
            summary.cardNumber = lane.cardNumber;
        if (summary.odoStart === null && lane.odoStart !== null)
            summary.odoStart = lane.odoStart;
        if (lane.odoEnd !== null)
            summary.odoEnd = lane.odoEnd;
        for (const kind of KINDS)
            summary.totals[kind] += lane.totals[kind];
    }
    return summary;
}
function kindLabel(kind, strings) {
    switch (kind) {
        case "driving":
            return strings.driving;
        case "work":
            return strings.work;
        case "rest":
            return strings.rest;
        case "availability":
            return strings.availability;
        default:
            return strings.noCard;
    }
}
function labelStepFor(pixelsPerHour) {
    if (pixelsPerHour >= 34)
        return 1;
    if (pixelsPerHour >= 22)
        return 2;
    if (pixelsPerHour >= 14)
        return 3;
    if (pixelsPerHour >= 9)
        return 6;
    return 12;
}
function gridStepFor(pixelsPerHour) {
    if (pixelsPerHour >= 200)
        return 15;
    if (pixelsPerHour >= 85)
        return 30;
    return 60;
}
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
function clampView(view) {
    const from = clamp(view.from, 0, 0.999);
    const to = clamp(view.to, from + 0.001, 1);
    return { from, to };
}
export { KINDS };
